/**
 * EditTicketForm — billing edits an approved ticket (#50 PR C, plan §2c, DEC-356).
 *
 * Volume (single-capture tickets), hours (quarter-hours), each extra / product quantity
 * with a remove (✕), "Add extra" from the active catalog, the disposal fee line (removable;
 * its quantity follows the volume) and "Add disposal fee" (#52), and a required reason. A live
 * preview prices every line with the shared lineAmountCents, the same helper the server and
 * the invoice use. Plain numeric text inputs: the BBLInput / FeetInchesPicker rule is
 * mobile-only. The reason is kept as typed (a notes field; DEC-147 doesn't apply).
 *
 * The API is the authority (EDIT_BILLING_TICKET, locks, stale-save guard). A refused save
 * names why, and the page reloads the ticket so the next attempt starts from the truth.
 */

import React from 'react';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';
import { ApiError } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';

import { formatUsdFromCents } from '../../lib/money';
import {
  useEditBillingTicket,
  useServiceChargeSearch,
  type DisposalFeeOffer,
  type TicketPageResponse,
} from './hooks';
import {
  REASON_MAX,
  buildBody,
  initialForm,
  preview,
  saveErrorMessage,
  validate,
  type TicketEditForm,
} from './ticket-edit-form';

const INPUT: React.CSSProperties = {
  height: '34px',
  width: '110px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  padding: '0 8px',
  fontSize: '14px',
  textAlign: 'right',
};
const ERROR: React.CSSProperties = { fontSize: '12px', color: 'var(--color-danger)' };
const NOTE: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const ROW: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(140px, 1fr) auto auto auto',
  alignItems: 'center',
  gap: '10px',
  padding: '6px 0',
  borderBottom: '1px solid var(--color-border-secondary)',
};

function newClientId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * "Add disposal fee": the fee the server resolved for this ticket's customer, site and day.
 * No fee set → say so and link the fee page (append-only prices; set it there, then reopen);
 * a deliberate $0.00 → nothing to add.
 */
function DisposalFeeOfferRow({
  offer,
  adding,
  feeCents,
  onToggle,
}: {
  offer: DisposalFeeOffer;
  adding: boolean;
  feeCents: number;
  onToggle: () => void;
}): React.JSX.Element {
  const site = offer.site.name;
  if (offer.feeCentsPerBbl === null) {
    const params = new URLSearchParams({ kind: offer.site.kind, placeId: offer.site.id });
    if (offer.haulerOperatorId !== null) {
      params.set('tab', 'customer');
      params.set('customerId', offer.haulerOperatorId);
    }
    return (
      <p style={{ ...NOTE, padding: '6px 0' }} data-testid="disposal-fee-missing">
        No disposal fee is set for this customer at {site} on {offer.day}.{' '}
        <a href={`/disposal-fees?${params.toString()}`} style={{ textDecoration: 'underline' }}>
          Set it in Customer disposal fees
        </a>
        , then reopen this ticket to add it.
      </p>
    );
  }
  if (offer.feeCentsPerBbl === 0) {
    return (
      <p style={{ ...NOTE, padding: '6px 0' }}>
        The disposal fee at {site} is $0.00, so there is nothing to add.
      </p>
    );
  }
  const rate = `${formatUsdFromCents(offer.feeCentsPerBbl)}/bbl`;
  if (!adding) {
    return (
      <div style={{ padding: '10px 0 0' }}>
        <Button variant="outline" onClick={onToggle}>
          Add disposal fee ({site} · {rate})
        </Button>
      </div>
    );
  }
  return (
    <div style={ROW} data-testid="disposal-fee-add">
      <span>
        Disposal fee – {site} <span style={NOTE}>(new, {rate}, bills the ticket volume)</span>
      </span>
      <span style={{ fontSize: '14px' }}>{formatUsdFromCents(feeCents)}</span>
      <Button variant="ghost" aria-label="Don't add the disposal fee" onClick={onToggle}>
        <X size={14} aria-hidden="true" />
      </Button>
      <span />
    </div>
  );
}

export function EditTicketForm({
  page,
  onSaved,
  onCancel,
  onConflict,
}: {
  page: TicketPageResponse;
  onSaved: () => void;
  onCancel: () => void;
  /** A 409: the page reloads the ticket and closes the form. */
  onConflict: () => void;
}): React.JSX.Element {
  const [form, setForm] = React.useState<TicketEditForm | null>(() => initialForm(page));
  const [submitted, setSubmitted] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [picking, setPicking] = React.useState(false);
  const charges = useServiceChargeSearch(search);

  const save = useEditBillingTicket(page.ticket.jobId, () => {
    toast.success('Ticket updated');
    onSaved();
  });

  if (form === null) return <p style={NOTE}>This ticket can’t be edited.</p>;

  const update = (fn: (f: TicketEditForm) => void): void => {
    setForm((prev) => {
      if (prev === null) return prev;
      const next: TicketEditForm = {
        ...prev,
        volume: prev.volume === null ? null : { ...prev.volume },
        hours: prev.hours === null ? null : { ...prev.hours },
        lines: prev.lines.map((l) => ({ ...l })),
        adds: prev.adds.map((a) => ({ ...a })),
      };
      fn(next);
      return next;
    });
  };

  const errors = validate(form);
  const shown = (key: string): string | undefined => (submitted ? errors[key] : undefined);
  const live = preview(page, form);
  const body = buildBody(form);

  const onSave = (): void => {
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    if (body === null) {
      toast.info('Nothing changed.');
      return;
    }
    save.mutate(body, {
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : null;
        toast.error(saveErrorMessage(code));
        if (err instanceof ApiError && err.status === 409) onConflict();
      },
    });
  };

  return (
    <section
      aria-label="Edit ticket"
      style={{
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
        padding: '16px',
        marginBottom: '24px',
      }}
    >
      <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '10px' }}>Edit ticket</h3>

      {form.volume !== null && (
        <div style={ROW}>
          <label htmlFor="edit-volume">
            Net volume (bbl)
            {!form.volume.changesBill && <div style={NOTE}>Doesn’t change the bill.</div>}
          </label>
          <input
            id="edit-volume"
            inputMode="decimal"
            style={INPUT}
            value={form.volume.value}
            onChange={(e): void => {
              const v = e.target.value;
              update((f) => {
                if (f.volume !== null) f.volume.value = v;
              });
            }}
          />
          <span style={NOTE}>was {form.volume.loaded}</span>
          <span style={ERROR}>{shown('volume')}</span>
        </div>
      )}

      {form.hours !== null && (
        <div style={ROW}>
          <label htmlFor="edit-hours">Hours (quarter-hours)</label>
          <input
            id="edit-hours"
            inputMode="decimal"
            style={INPUT}
            value={form.hours.value}
            onChange={(e): void => {
              const v = e.target.value;
              update((f) => {
                if (f.hours !== null) f.hours.value = v;
              });
            }}
          />
          <span style={NOTE}>was {form.hours.loaded}</span>
          <span style={ERROR}>{shown('hours')}</span>
        </div>
      )}

      {form.lines.map((l) => (
        <div key={l.eventId} style={{ ...ROW, opacity: l.removed ? 0.5 : 1 }}>
          <label htmlFor={`edit-line-${l.eventId}`}>
            {l.label}
            {l.removed && <div style={NOTE}>Will be removed</div>}
            {l.followsVolume && !l.removed && (
              <div style={NOTE}>
                Bills the ticket volume
                {live.lines.get(l.eventId) !== undefined &&
                  ` · ${formatUsdFromCents(live.lines.get(l.eventId) ?? 0)}`}
              </div>
            )}
          </label>
          <input
            id={`edit-line-${l.eventId}`}
            inputMode="decimal"
            style={INPUT}
            disabled={l.removed || l.followsVolume}
            value={l.value}
            onChange={(e): void => {
              const v = e.target.value;
              update((f) => {
                const t = f.lines.find((x) => x.eventId === l.eventId);
                if (t !== undefined) t.value = v;
              });
            }}
          />
          <Button
            variant="ghost"
            aria-label={l.removed ? `Keep ${l.label}` : `Remove ${l.label}`}
            onClick={(): void => {
              update((f) => {
                const t = f.lines.find((x) => x.eventId === l.eventId);
                if (t !== undefined) t.removed = !t.removed;
              });
            }}
          >
            {l.removed ? 'Undo' : <X size={14} aria-hidden="true" />}
          </Button>
          <span style={ERROR}>{shown(`line:${l.eventId}`)}</span>
        </div>
      ))}

      {form.adds.map((a) => (
        <div key={a.clientId} style={ROW}>
          <label htmlFor={`edit-add-${a.clientId}`}>
            {a.label} <span style={NOTE}>(new, {formatUsdFromCents(a.rateCents)} each)</span>
          </label>
          <input
            id={`edit-add-${a.clientId}`}
            inputMode="decimal"
            style={INPUT}
            value={a.quantity}
            onChange={(e): void => {
              const v = e.target.value;
              update((f) => {
                const t = f.adds.find((x) => x.clientId === a.clientId);
                if (t !== undefined) t.quantity = v;
              });
            }}
          />
          <Button
            variant="ghost"
            aria-label={`Don't add ${a.label}`}
            onClick={(): void => {
              update((f) => {
                f.adds = f.adds.filter((x) => x.clientId !== a.clientId);
              });
            }}
          >
            <X size={14} aria-hidden="true" />
          </Button>
          <span style={ERROR}>{shown(`add:${a.clientId}`)}</span>
        </div>
      ))}

      {/* #52 (DEC-358 #9) — a ticket approved before disposal fees were switched on. */}
      {form.feeOffer !== null && (
        <DisposalFeeOfferRow
          offer={form.feeOffer}
          adding={form.addFee}
          feeCents={live.feeCents}
          onToggle={(): void => {
            update((f) => {
              f.addFee = !f.addFee;
            });
          }}
        />
      )}

      <div style={{ padding: '10px 0' }}>
        {!picking ? (
          <Button
            variant="outline"
            onClick={(): void => {
              setPicking(true);
            }}
          >
            Add extra
          </Button>
        ) : (
          <div>
            <input
              aria-label="Search extras"
              placeholder="Search extras"
              style={{ ...INPUT, width: '240px', textAlign: 'left' }}
              value={search}
              onChange={(e): void => {
                setSearch(e.target.value);
              }}
            />
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                marginTop: '6px',
                maxHeight: '200px',
                overflow: 'auto',
              }}
            >
              {(charges.data?.serviceCharges ?? []).map((c) => (
                <li key={c.id}>
                  <Button
                    variant="ghost"
                    onClick={(): void => {
                      update((f) => {
                        f.adds.push({
                          clientId: newClientId(),
                          serviceChargeId: c.id,
                          label: c.label,
                          rateCents: c.defaultRateCents,
                          quantity: '1',
                        });
                      });
                      setPicking(false);
                      setSearch('');
                    }}
                  >
                    {c.label} · {formatUsdFromCents(c.defaultRateCents)} / {c.unitOfMeasure}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <label
        htmlFor="edit-reason"
        style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}
      >
        Reason
      </label>
      <textarea
        id="edit-reason"
        maxLength={REASON_MAX}
        rows={2}
        value={form.reason}
        onChange={(e): void => {
          const v = e.target.value;
          update((f) => {
            f.reason = v;
          });
        }}
        style={{
          width: '100%',
          borderRadius: '10px',
          border: '1px solid var(--color-border-secondary)',
          backgroundColor: 'var(--color-bg-tertiary)',
          color: 'var(--color-text-primary)',
          padding: '8px',
          fontSize: '14px',
        }}
      />
      <span style={ERROR}>{shown('reason')}</span>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          marginTop: '12px',
        }}
      >
        <span style={{ fontSize: '14px' }}>
          New unbilled total (pre-tax):{' '}
          <strong data-testid="preview-total">{formatUsdFromCents(live.totalCents)}</strong>{' '}
          <span style={NOTE}>was {formatUsdFromCents(page.unbilledTotalCents)}</span>
        </span>
        <span style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outline" onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={save.isPending}>
            {save.isPending && (
              <Loader2
                size={14}
                aria-hidden="true"
                style={{ marginRight: '6px' }}
                className="animate-spin"
              />
            )}
            Save
          </Button>
        </span>
      </div>
    </section>
  );
}
